import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { getPublicEnv } from '@/lib/env';
import type { ApiEnvelope, ApiFailureEnvelope } from '@/types/api';
import type { AuthSessionPayload } from '@/types/auth';

import { useAuthStore } from '@/store/auth-store';
import { isJwtExpired } from '@/utils/jwt-expiry';

const REQUEST_TIMEOUT_MS = 30_000;

export function parseApiFailure(error: AxiosError<ApiFailureEnvelope>): ApiFailureEnvelope {
  const body = error.response?.data;
  if (body && body.success === false) {
    return body;
  }
  return {
    success: false,
    message: error.message || 'Unable to reach the service.',
    errorCode: 'NETWORK_ERROR',
  };
}

export function createApiClient(): AxiosInstance {
  const { apiBaseUrl } = getPublicEnv();

  const bare = axios.create({
    baseURL: apiBaseUrl,
    timeout: REQUEST_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    validateStatus: (status) => status >= 200 && status < 300,
  });

  let refreshPromise: Promise<string | null> | null = null;

  async function refreshAccessToken(): Promise<string | null> {
    const { refreshToken, updateTokens, clearSession } = useAuthStore.getState();
    if (!refreshToken) {
      clearSession();
      return null;
    }

    try {
      const response = await bare.post<
        ApiEnvelope<
          Pick<AuthSessionPayload, 'accessToken' | 'refreshToken'> & {
            user: AuthSessionPayload['user'];
            roles: string[];
            permissions: string[];
          }
        >
      >('/api/v1/auth/refresh', { refreshToken });

      const envelope = response.data;
      if (!envelope.success) {
        clearSession();
        return null;
      }

      updateTokens(envelope.data.accessToken, envelope.data.refreshToken);
      return envelope.data.accessToken;
    } catch {
      clearSession();
      return null;
    }
  }

  function enqueueRefresh(): Promise<string | null> {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  }

  const client = axios.create({
    baseURL: apiBaseUrl,
    timeout: REQUEST_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    validateStatus: (status) => status >= 200 && status < 300,
  });

  function shouldSkipAuth(url?: string): boolean {
    return Boolean(
      url?.includes('/auth/login') ||
        url?.includes('/auth/register') ||
        url?.includes('/auth/refresh'),
    );
  }

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (shouldSkipAuth(config.url)) {
      return config;
    }

    let token: string | null = useAuthStore.getState().accessToken || null;

    if (token && isJwtExpired(token)) {
      token = await enqueueRefresh();
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiFailureEnvelope>) => {
      const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      const status = error.response?.status;
      const url = originalConfig?.url ?? '';

      if (
        status !== 401 ||
        originalConfig._retry ||
        shouldSkipAuth(url) ||
        url.includes('/auth/logout')
      ) {
        return Promise.reject(error);
      }

      originalConfig._retry = true;

      const newAccess = await enqueueRefresh();

      if (!newAccess || !originalConfig.headers) {
        return Promise.reject(error);
      }

      originalConfig.headers.Authorization = `Bearer ${newAccess}`;
      return client(originalConfig);
    },
  );

  return client;
}

export const apiClient = createApiClient();
